import React, { useEffect, useState } from "react";
import NavBar from "../components/layout/NavBar";
import FlowRunnerCore from "../components/flow/FlowRunnerCore";
import { FlowStore } from "../components/flow/FlowTypes";
import { getFlows } from "../api/flowApi";
import { useAuth } from "../components/auth/AuthContext";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../components/common/common.css";

export default function FlowRunner() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [flows, setFlows] = useState(() => FlowStore.loadAll());
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [selectedFlowName, setSelectedFlowName] = useState(
    (location?.state && location.state.flowName) ||
    params.get("flow") ||
    ""
  );

  // Load flows from server; fall back to localStorage cache
  useEffect(() => {
    if (!token) return;
    setLoadingFlows(true);
    getFlows(token)
      .then((serverFlows) => {
        if (Array.isArray(serverFlows) && serverFlows.length > 0) {
          FlowStore.syncFromServer(serverFlows);
          setFlows(serverFlows);
          if (!selectedFlowName) setSelectedFlowName(serverFlows[0]?.name || "");
        }
      })
      .catch(() => {
        // Keep localStorage cache on network error
      })
      .finally(() => setLoadingFlows(false));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeFlow = flows.find((f) => f.name === selectedFlowName) || flows[0] || null;

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <header className="top-bar">
        <div className="ai-chip">{t("menu.flowRunner", "Flow Runner")}</div>
        <div style={{ flex: 1 }} />

        {/* Flow selector */}
        {flows.length > 1 && (
          <select
            value={selectedFlowName}
            onChange={(e) => setSelectedFlowName(e.target.value)}
            style={{
              padding: "6px 10px",
              border: "1.5px solid #222",
              borderRadius: 8,
              background: "#fff",
              fontSize: 13,
            }}
          >
            {flows.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name}
              </option>
            ))}
          </select>
        )}
      </header>

      <div className="main-layout" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <section className="center-panel">
          {loadingFlows && !activeFlow && (
            <div className="skeleton-line" style={{ width: "60%", marginBottom: 12 }} />
          )}

          {!activeFlow && !loadingFlows && (
            <div style={{ color: "#888" }}>
              {t("flow.noFlow", "No flows available. Create one in Flow Designer.")}
            </div>
          )}

          {activeFlow && (
            <FlowRunnerCore
              flow={activeFlow}
              token={token}
              showHeader={true}
              showContextPanel={false}
            />
          )}
        </section>

        <aside className="detail-panel">
          {/* Saved flow list for quick switching */}
          {flows.length > 0 && (
            <div>
              <h4 style={{ margin: "0 0 8px" }}>
                {t("flowDesigner.savedFlows", "Saved Flows")}
              </h4>
              <ul style={{ listStyle: "none", padding: 0 }}>
                {flows.map((f) => (
                  <li
                    key={f.name}
                    onClick={() => setSelectedFlowName(f.name)}
                    style={{
                      ...styles.flowItem,
                      background: f.name === (activeFlow?.name) ? "#f0f0f0" : "#fff",
                      fontWeight: f.name === (activeFlow?.name) ? 600 : 400,
                    }}
                  >
                    {f.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

const styles = {
  flowItem: {
    padding: "8px 12px",
    border: "1px solid #ddd",
    borderRadius: 8,
    marginBottom: 6,
    cursor: "pointer",
    fontSize: 13,
  },
};
